import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const recordedVoiceoverVideoBasicsSkill: Skill = {
    name: SKILL_NAMES.recordedVoiceoverVideoBasics,
    description: `The user is driving the video with their own recorded/uploaded voiceover AUDIO — a human recording, not AI-generated and not a video of someone speaking. Load BEFORE building that video. For the shared overlay/coverage/scene-group mechanics, use ${SKILL_NAMES.voiceoverVideoBasics}.`,
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Recorded Voiceover Video Basics

Building a video over a human voiceover the user recorded — not AI-generated. The
shared scene-group mechanics — overlay coverage, splits, reorders, re-timing,
targeting — live in \`${SKILL_NAMES.voiceoverVideoBasics}\`; this skill is the recorded-specific
part.

## Sourcing the audio
The voiceover is an audio recording the user provides. Never draft or AI-generate one.
- If the user attached a recording, take its \`audio_id\` from the
  \`### AUDIO: ... | ID: ... ###\` metadata.
- Otherwise call \`${TOOL_NAMES.searchUploadedVoiceovers}\` to find a prior upload and use its id.
- If you still can't find any audio, ask the user to attach or record one — do NOT
  draft or generate narration.

Call \`${TOOL_NAMES.addRecordedVoiceoverSceneGroup}\` with that audio_id — a single scene group for the
whole recording; it transcribes the recording and syncs the transcript
automatically. Pass \`language_code\` only if the user specified a language. Then build
overlays over that voiceover.

## Not this skill
- A *video* of someone speaking (or a narrated screen recording) is a talking head —
  use \`${SKILL_NAMES.talkingHeadVideoBasics}\`.
- An audiogram/waveform treatment over the audio is a different build — stop and pick
  the video skill for it from Available Skills.
- Narration generated from a script is an AI voiceover — use \`${SKILL_NAMES.aiVoiceoverVideoBasics}\`.

## Pacing the overlays
The recording sets the video's length, so the overlay count follows from it: **one overlay
every 8-12 words** (about four seconds of speech), never more than 15. That is ~15 overlays a
minute — a 7-minute recording is ~100. Don't cover a long recording with a few long overlays;
a scene held for 20+ seconds is a defect, not a simplification.

For anything over about 90 seconds, build **one minute of narration at a time**: one
\`${TOOL_NAMES.addVoiceoverScene}\` call with that minute's 12-15 overlays, then set their media
and text, then the next minute. Each call only writes out ~150 words, so there's no reason to
widen spans as you go. Lint once the last minute is covered.

## Editing
The recording is the user's real voice: unlike an AI voiceover, its words can't be
reworded, re-voiced, or re-synthesized. Edit the video by adding, re-timing, and
removing overlays (\`${SKILL_NAMES.voiceoverVideoBasics}\`), not by changing the narration.`;
    },
};
